import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  getInboxConversation,
  getInboxMessages,
  handBackInboxConversation,
  replyToInboxConversation,
  takeOverInboxConversation,
} from "api/api.service";
import usePolling from "./usePolling";
import { LEAD_STAGE_LABEL, formatTime, isFailedStatus, newClientMessageId, statusLabel, timeAgo } from "./inboxFormat";
import { ChannelBadge } from "./ConversationList";

// With the live stream connected, polling is only a safety net.
const CHAT_POLL_MS = { live: 15000, fallback: 4000 };
const MAX_REPLY = 4000;

function LeadFacts({ c }) {
  const facts = [
    c.phone,
    c.role,
    c.schoolName,
    c.district,
    c.email,
    c.timezone && `Time zone: ${c.timezone}`,
    c.channel === "WEB" && c.pageUrl && `Opened on ${c.pageUrl}`,
  ].filter(Boolean);
  return <p className="mcap-inbox-facts">{facts.join(" · ")}</p>;
}

/** The same person's other chats, by email (a returning website visitor starts a new chat each time). */
function OtherChats({ chats, onOpen }) {
  if (!chats?.length) return null;
  return (
    <p className="mcap-inbox-other">
      Also chatted:{" "}
      {chats.map((o, i) => (
        <span key={o.id}>
          {i > 0 && ", "}
          <button type="button" className="mcap-inbox-linkish" onClick={() => onOpen(o.id)}>
            {o.channel === "WEB" ? "website" : "WhatsApp"}, {formatTime(o.createdAt)}
          </button>
        </span>
      ))}
    </p>
  );
}

function Bubble({ m }) {
  if (m.senderType === "SYSTEM") {
    return (
      <li className="mcap-inbox-system">
        <span>{m.body}</span> <time dateTime={m.createdAt}>{formatTime(m.createdAt)}</time>
      </li>
    );
  }
  const mine = m.direction === "OUT";
  const who = m.senderType === "AI" ? "AI assistant" : m.senderType === "HUMAN" ? m.sentByName || "Team" : null;
  const failed = isFailedStatus(m.status);
  const discarded = m.status === "discarded";
  return (
    <li className={`mcap-inbox-msg ${mine ? "is-out" : "is-in"} is-${m.senderType.toLowerCase()}${failed ? " is-failed" : ""}${discarded ? " is-discarded" : ""}`}>
      {who && <span className="mcap-inbox-msg-who">{who}</span>}
      <div className="mcap-inbox-bubble">
        {m.body ? m.body : m.mediaUrl ? <em>Sent a voice note, image or file. Open it in Twilio to listen or view.</em> : null}
      </div>
      <span className="mcap-inbox-msg-meta">
        <time dateTime={m.createdAt}>{formatTime(m.createdAt)}</time>
        {mine && statusLabel(m.status) && <> · {statusLabel(m.status)}</>}
        {(failed || discarded) && m.errorReason && <> · {m.errorReason}</>}
      </span>
    </li>
  );
}

const activeAgo = (iso) => {
  const t = timeAgo(iso);
  return !t ? "they see it while the chat is open" : t === "now" ? "active just now" : `active ${t} ago`;
};

function Composer({ conversation, busy, onSend }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  // One key per message being written, kept across retries of that message,
  // so a double-click or a retry after a timeout sends it once.
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = newClientMessageId();

  if (conversation.optedOut) {
    return <p className="mcap-inbox-notice">This person replied STOP, so they cannot be messaged. If they write START, chat reopens.</p>;
  }
  const isWeb = conversation.channel === "WEB";
  if (isWeb && !conversation.windowOpen) {
    return (
      <p className="mcap-inbox-notice">
        This visitor&apos;s website chat has ended
        {conversation.lastSeenAt ? ` (last active ${formatTime(conversation.lastSeenAt)})` : ""}, so they would not see a reply
        here.{conversation.email ? ` Email them at ${conversation.email} instead.` : ""}
      </p>
    );
  }
  if (!conversation.windowOpen) {
    return (
      <p className="mcap-inbox-notice">
        WhatsApp only allows a reply within 24 hours of their last message
        {conversation.lastInboundAt ? ` (${formatTime(conversation.lastInboundAt)})` : ""}. When they write again, you can reply here.
      </p>
    );
  }
  if (!conversation.aiPaused) {
    return <p className="mcap-inbox-notice">The AI is answering this chat. Take over to reply yourself.</p>;
  }

  const send = async () => {
    const message = text.trim();
    if (!message || sending) return;
    setSending(true);
    try {
      const res = await onSend(message, idRef.current);
      if (res?.data?.sent === false) return; // stays in the box; the chat shows why
      setText("");
      idRef.current = newClientMessageId();
    } catch {
      // errorHandler already showed why; the draft stays so nothing is lost.
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      className="mcap-inbox-composer"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        placeholder={
          isWeb
            ? `Reply in their website chat (${activeAgo(conversation.lastSeenAt)}). Enter sends.`
            : "Write a reply. Enter sends, Shift+Enter adds a line."
        }
        aria-label="Reply"
        maxLength={MAX_REPLY}
        rows={2}
        disabled={busy}
      />
      <button type="submit" className="mcap-lic-btn" disabled={!text.trim() || sending || busy}>
        {sending ? "Sending…" : "Send"}
      </button>
    </form>
  );
}

export default function ChatPane({ conversationId, live, liveTick, onChanged, onBack, onOpen = () => {} }) {
  const fetchChat = useCallback(
    async (signal) => {
      const [detail, messages] = await Promise.all([
        getInboxConversation(conversationId, signal),
        getInboxMessages(conversationId, {}, signal),
      ]);
      return { conversation: detail?.data ?? null, messages: messages?.data ?? [] };
    },
    [conversationId],
  );
  const chat = usePolling(fetchChat, `chat:${conversationId}`, live ? CHAT_POLL_MS.live : CHAT_POLL_MS.fallback);
  // A live event for this conversation: fetch now.
  const refreshChat = chat.refresh;
  useEffect(() => {
    if (liveTick) refreshChat();
  }, [liveTick, refreshChat]);
  const [busy, setBusy] = useState(false);

  const scroller = useRef(null);
  const stickToBottom = useRef(true);
  const count = chat.data?.messages.length ?? 0;
  useEffect(() => {
    stickToBottom.current = true;
  }, [conversationId]);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [count, conversationId]);

  const act = async (fn) => {
    setBusy(true);
    try {
      return await fn();
    } finally {
      setBusy(false);
      chat.refresh();
      onChanged();
    }
  };

  const c = chat.data?.conversation;

  if (chat.error && !chat.data) {
    return (
      <section className="mcap-inbox-chat mcap-inbox-pad">
        {onBack && (
          <button type="button" className="mcap-inbox-back" onClick={onBack}>
            <i className="mdi mdi-chevron-left" aria-hidden="true" /> Conversations
          </button>
        )}
        <div className="mcap-lic-error-card" role="alert">
          <h3>This conversation could not load.</h3>
          <p>The request failed; nothing was lost. {chat.error.message || ""}</p>
          <button type="button" className="mcap-lic-btn" onClick={chat.refresh}>
            Try again
          </button>
        </div>
      </section>
    );
  }
  if (!c) {
    return (
      <section className="mcap-inbox-chat mcap-inbox-pad">
        <p className="mcap-inbox-muted">Loading the conversation…</p>
      </section>
    );
  }

  const mode = c.aiPaused
    ? c.pausedByName
      ? `${c.pausedByName} is replying`
      : "Waiting for a person"
    : c.optedOut
      ? "Opted out"
      : "AI is replying";

  return (
    <section className="mcap-inbox-chat" aria-label={`Conversation with ${c.displayName}`}>
      <header className="mcap-inbox-chat-head">
        {onBack && (
          <button type="button" className="mcap-inbox-back" onClick={onBack}>
            <i className="mdi mdi-chevron-left" aria-hidden="true" /> Conversations
          </button>
        )}
        <div className="mcap-inbox-chat-id">
          <h2>{c.displayName}</h2>
          <LeadFacts c={c} />
          <OtherChats chats={c.otherChats} onOpen={onOpen} />
          <div className="mcap-inbox-chat-tags">
            <ChannelBadge channel={c.channel} />
            <span className="mcap-lic-chip">{LEAD_STAGE_LABEL[c.leadStage] ?? c.leadStage}</span>
            {c.hasAccount && <span className="mcap-inbox-tag is-account">Has an account</span>}
            <span className={`mcap-inbox-mode ${c.aiPaused ? "is-human" : "is-ai"}`}>{mode}</span>
            {c.needsHuman && <span className="mcap-inbox-tag is-needs">Needs a person</span>}
          </div>
        </div>
        <div className="mcap-inbox-chat-actions">
          {c.aiPaused ? (
            <button type="button" className="mcap-lic-btn is-ghost" disabled={busy || c.optedOut} onClick={() => act(() => handBackInboxConversation(c.id))}>
              Hand back to AI
            </button>
          ) : (
            <button type="button" className="mcap-lic-btn" disabled={busy || c.optedOut} onClick={() => act(() => takeOverInboxConversation(c.id))}>
              Take over
            </button>
          )}
        </div>
      </header>

      {chat.error && (
        <p className="mcap-inbox-reconnecting" role="status">
          Connection lost. Retrying…
        </p>
      )}
      <ol
        className="mcap-inbox-thread"
        ref={scroller}
        aria-live="polite"
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        {chat.data.messages.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
        {c.aiReplying && (
          <li className="mcap-inbox-typing" aria-label="The AI is writing a reply">
            AI is replying…
          </li>
        )}
      </ol>

      <Composer
        key={c.id}
        conversation={c}
        busy={busy}
        onSend={(message, clientMessageId) => act(() => replyToInboxConversation(c.id, message, clientMessageId))}
      />
    </section>
  );
}
